import type { UserJourneyNode } from '@com/components/user-journey/type'
import type { UserJourneyData } from '@pt/api/insights/user-journey/type'
import { cloneDeep } from 'lodash-es'
import { longestPath } from '@com/components/user-journey/utils'
import {
  // ProjectListOptionGroup,
  ProjectListOption,
} from '@pt/api/insights/index.type'

// 用户旅程基础埋点配置
export const basicAction: UserJourneyNode[] = [
  {
    label: 'gs',
    id: 'gs',
    edges: [],
  },
  {
    label: 'ws',
    id: 'ws',
    edges: [],
  },
  {
    label: 'impression',
    id: 'impression',
    edges: [],
  },
]

/**
 * 根据埋点数据动态添加ws,gs,impression基础埋点，通过是否有埋点数据控制基础埋点是否添加
 */
export function wrapBasicAction(
  nodes: UserJourneyNode[],
  data: null | UserJourneyData[],
): UserJourneyNode[] {
  const basicActionIds = basicAction.map((action) => action.id)
  // 过滤节点列表中的基本埋点
  const nodeList = [
    ...nodes.filter((item) => !basicActionIds.includes(item.id)),
  ]
  // gs不需要根据data判断，默认添加
  const dataAction = data
    ? `${data.map((item) => item.action).join(',')},gs`
    : ''
  const tempNodes = data
    ? basicAction.filter((action) => dataAction.indexOf(action.id) !== -1)
    : []
  const journeyPath = longestPath(nodes)
  tempNodes.forEach((node, index) => {
    if (index !== 0) {
      const lastNode = tempNodes[index - 1] || null
      node.edges = lastNode ? [lastNode.id] : []
    } else {
      node.edges = journeyPath[0] ? [journeyPath[0]] : []
    }
    nodeList.unshift(node)
  })

  return nodeList
}

export function patchData(
  nodes: UserJourneyNode[],
  data: null | UserJourneyData[],
): UserJourneyNode[] {
  const copied = cloneDeep(nodes)
  if (!data) {
    return copied
  }

  return nodes.map<UserJourneyNode>((node) => {
    const items = data.filter((item) => item.action === node.id)

    const reach = items.reduce((acc, { reach }) => acc + reach, 0)
    const install = items.reduce((acc, { install }) => acc + install, 0)

    const nexts: UserJourneyNode['nexts'] = {}

    data.forEach((d) => {
      if (d.pre_action === node.id) {
        nexts[d.action] = {
          timing: d.cost_time,
          flows: d.reach,
        }
      }
    })

    const flows = Object.values(nexts).reduce(
      (acc, { flows }) => acc + flows,
      0,
    )
    const flowsRate = reach === 0 ? 0 : Number((flows / reach).toFixed(4))

    const installRate = reach === 0 ? 0 : Number((install / reach).toFixed(4))

    // 流失数 = 到达数 - 跳转数 - 流转到下一节点数
    const loss = reach - install - flows
    const lossRate =
      reach === 0 ? 0 : Number((1 - installRate - flowsRate).toFixed(4))

    return {
      ...node,
      // 旅程的下一个节点集合
      nexts: Object.keys(nexts).length ? nexts : undefined,
      // 到达数
      reach,
      // 流转到下一节点数
      flows,
      flowsRate,
      // 跳转数
      install,
      installRate,
      // 流失数 = 到达数 - 跳转数 - 流转到下一节点数
      loss,
      lossRate,
    }
  })
}

export function getCustomActionMap(
  nodes: UserJourneyNode[],
): Record<string, string> {
  const map: Record<string, string> = {}
  const basicActionNames: string[] = []
  for (let i = 1; i <= 15; i++) {
    basicActionNames.push(`action${i}`)
  }
  nodes.forEach((node) => {
    if (!basicActionNames.includes(node.label)) {
      map[node.id] = node.label
    }
  })
  return map
}

// 判断当前项目列表是否包含选中的项目，根据项目id和baseid确定一个唯一的项目
export function hasCurrentProject(
  list: ProjectListOption[],
  projectId?: number,
  basicId?: number,
): boolean {
  return list.some(
    (item) => item.project_id === projectId && item.basic_id === basicId,
  )
}
